import React from "react";
import { Edit, useForm } from "@refinedev/antd";
import { Form, Button, Card, Typography, Descriptions, Tag, Alert, Space } from "antd";
import { useShow, useCustomMutation } from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import { ReloadOutlined } from "@ant-design/icons";

const { Text } = Typography;

export const SaleEdit: React.FC = () => {
  const { form, formProps, id } = useForm({
    resource: "sales",
    meta: {
      fields: [
        "id",
        "invoiceNo",
        "customerTPIN",
        "customerName",
        "salesStatusCode",
        "currencyTypeCode",
        "remark",
        "status",
        "createdAt",
        "updatedAt"
      ]
    },
    redirect: "show",
    warnWhenUnsavedChanges: true
  });

  const navigate = useNavigate();

  // We'll use useShow to display the current data
  const { queryResult } = useShow({
    resource: "sales",
    id,
    meta: {
      fields: [
        "id",
        "invoiceNo",
        "customerTPIN",
        "customerName",
        "salesStatusCode",
        "currencyTypeCode",
        "remark",
        "totalItemCount",
        "totalTaxableAmount",
        "totalTax",
        "totalAmount",
        "isActive",
        "status",
        "createdAt",
        "updatedAt"
      ]
    }
  });

  const { mutate: resubmitSale } = useCustomMutation();

  const { data, isLoading } = queryResult;
  const record = data?.data;

  // If the sale is not in PENDING status, redirect to show page
  React.useEffect(() => {
    if (record && record.status !== "PENDING") {
      navigate(`/sales/show/${record.id}`);
    }
  }, [record, navigate]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "PENDING":
        return "orange";
      case "COMPLETED":
        return "green";
      case "CANCELLED":
        return "red";
      default:
        return "default";
    }
  };

  const handleResubmit = async () => {
    try {
      await resubmitSale({
        url: `sales/${record?.id}/resubmit`,
        method: "post",
        successNotification: () => ({
          message: "Success",
          description: "Sale resubmitted for fiscalization successfully",
          type: "success",
        }),
      });
      navigate(`/sales/show/${record?.id}`);
    } catch (error) {
      console.error('Resubmit error:', error);
    }
  };

  if (!record || record.status !== "PENDING") {
    return null;
  }

  return (
    <Edit
      isLoading={isLoading}
      saveButtonProps={{
        style: { display: 'none' }
      }}
    >
      <Card title="Sale Fiscalization" style={{ marginBottom: 16 }}>
        <Alert
          message="Resubmit Sale for Fiscalization"
          description="This sale is pending fiscalization. You can resubmit it to the tax authority for processing."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Descriptions bordered column={2}>
          <Descriptions.Item label="Invoice No">{record?.invoiceNo}</Descriptions.Item>
          <Descriptions.Item label="Customer">{record?.customerName}</Descriptions.Item>
          <Descriptions.Item label="Current Status">
            <Tag color={getStatusColor(record?.status)}>
              {record?.status}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Total Amount">
            <Text strong>{record?.totalAmount?.toFixed(2)} {record?.currencyTypeCode}</Text>
          </Descriptions.Item>
        </Descriptions>

        <Space style={{ marginTop: 16 }}>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={handleResubmit}
          >
            Resubmit for Fiscalization
          </Button>
        </Space>
      </Card>
    </Edit>
  );
}; 